import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { get, pickBy } from 'lodash';
import SidebarConversationsHeader from 'components/SidebarConversationsHeader';
import { Sidebar as RawSidebar, SidebarMenu, SidebarMenuItem } from 'ui/sidebar';
import {
   ButtonReset,
   MAIN_HEADER_HEIGHT,
   MAIN_HEADER_MAINTENANCE_HEIGHT,
   Media,
   media,
   MediaBody,
   MediaImage,
   NavLink,
   size,
   spacing,
} from 'ui';
import Avatar from './Avatar';
import { destroyNewConversationPlaceholder } from 'actions/ui';
import { capitalizeFirstLetter, formatRelativeDate } from 'utils';
import getAttachmentURL from 'utils/getAttachmentURL';
import { TopicMetaUpdated } from 'components/TopicCard';
import LinesEllipsis from 'react-lines-ellipsis';
import { useTranslation } from 'react-i18next';
import { gql } from 'apollo-boost';
import { useQuery } from '@apollo/react-hooks';
import { useDebounce } from 'react-use';
import { Scrollbars as RawScrollbars } from 'react-custom-scrollbars';
import { getSelectedPlace } from 'selectors';
import { history } from 'store';

const Scrollbars = styled(RawScrollbars)`
   height: calc(100vh - 64px) !important;
   ${media.tablet`
    height: calc(80vh - 64px) !important;
  `}
`;

const Sidebar = styled(RawSidebar)`
   ${media.tablet`
       position: fixed;
       top: ${MAIN_HEADER_HEIGHT};
       left: 250px;
       width: 300px !important;
       height: calc(100vh - 60px);

       ${({ isActiveMaintenance }) =>
          isActiveMaintenance &&
          css`
             top: ${MAIN_HEADER_MAINTENANCE_HEIGHT};
          `};
  `};
`;

const DeletePlaceholderButton = styled(ButtonReset)`
   cursor: pointer;
`;

const MessageContent = styled.div`
   ${props =>
      props.isUnread &&
      css`
         color: ${({ theme }) => theme.text};
         font-weight: 600;
      `};
`;

const Message = styled(NavLink)`
   padding: ${spacing()};
   position: relative;
   width: 100%;
   color: inherit;
   border-bottom: 1px solid ${({ theme }) => theme.separator};
   transition: background-color 150ms ease;
   min-height: ${spacing(4.5)};
   align-items: center;
   display: block;

   &:hover {
      background-color: ${({ theme }) => theme.background};
   }

   &.active,
   &.active:hover {
      background-color: ${({ theme }) => theme.background};
   }
`;

const MessageMeta = styled.span`
   float: right;
   color: ${({ theme }) => theme.textLight};
   font-size: ${size(-1)};
   line-height: 1.3;

   ${props =>
      props.isNewMessagePlaceholder &&
      css`
         font-size: ${size()};
         top: 50%;
         transform: translateY(-50%);
      `};

   ${props =>
      props.isNewMessage &&
      css`
         font-weight: 600;
      `};
`;

export const MessageAuthor = styled.h3`
   font-weight: 500;
   margin-top: 0;
   margin-bottom: ${spacing(0.125)};
   font-size: ${size(0)};
   white-space: nowrap;
   overflow: hidden;
   max-width: 220px;
   text-overflow: ellipsis;

   ${props =>
      props.isNewMessagePlaceholder &&
      css`
         margin-bottom: 0;
      `};
`;

export const MessageExcerpt = styled.div`
   margin-top: 0;
   margin-bottom: 0;
   color: ${({ theme }) => theme.textLight};
   font-size: ${size(-1)};
   word-break: break-word;
   white-space: pre-wrap;

   ${props =>
      props.isUnread &&
      css`
         color: ${({ theme }) => theme.text};
      `};
`;

const SidebarMessage = styled(SidebarMenuItem)`
   &:before {
      display: none !important;
   }
`;

const CONVERSATIONS = gql`
   query conversations(
      $placeId: ID
      $state: String
      $search: String
      $memberID: ID
      $agencyID: ID
      $inPortfolio: Boolean
      $after: String
   ) {
      allConversations(
         filter: { placeId: { eq: $placeId }, state: { eq: $state } }
         search: $search
         byMember: $memberID
         byAgency: $agencyID
         inPortfolio: $inPortfolio
         after: $after
         first: 20
         notArchivedOnly: true
         order: { by: updated_at, direction: desc }
      ) {
         pageInfo {
            hasNextPage
            endCursor
         }
         edges {
            node {
               id
               sqlId
               preview
               displayName
               read
               updatedAt
               author {
                  id
                  sqlId
                  displayName(placeId: $placeId)
               }
            }
         }
      }
   }
`;

const ConversationsSidebar = ({ hasActiveMessage }) => {
   const { t } = useTranslation();
   const dispatch = useDispatch();
   const newConversation = useSelector(state => state.ui.newConversation);
   const [search, setSearch] = useState('');
   const [debouncedSearch, setDebouncedSearch] = useState('');
   const scrollRef = useRef();

   const selectedPlaceUuid = useSelector(state => state.ui.selectedPlaceUuid || getSelectedPlace(state)?.uuid);
   const maintenanceData = useSelector(state => state.communication.data);

   useDebounce(
      () => {
         setDebouncedSearch(search);
      },
      300,
      [search]
   );

   const { data, loading, fetchMore } = useQuery(CONVERSATIONS, {
      variables: pickBy({
         search: debouncedSearch,
         placeId: selectedPlaceUuid,
      }),
   });

   const conversations = get(data, 'allConversations.edges', []).map(({ node }) => ({
      ...node,
   }));

   const handleClearSearch = () => {
      setSearch('');
   };

   const handleUpdateSearch = e => {
      setSearch(e.target.value);
   };

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            after: data?.allConversations?.pageInfo?.endCursor,
         },
         updateQuery: (prev, { fetchMoreResult }) => {
            let newResult = {
               ...prev,
               allConversations: {
                  ...prev.allConversations,
                  pageInfo: get(fetchMoreResult, 'allConversations.pageInfo', {}),
                  edges: [...prev.allConversations.edges, ...get(fetchMoreResult, 'allConversations.edges', [])],
               },
            };

            return newResult;
         },
      });
   };

   const handleNewConversationDelete = e => {
      e.preventDefault();
      history.push('/mes-echanges/mes-messages');
      dispatch(destroyNewConversationPlaceholder());
   };

   return (
      <Sidebar
         multi
         two
         hasHeader
         isLarge
         shouldBeHiddenOnSmallViewports={hasActiveMessage}
         isScrollable
         isActiveMaintenance={maintenanceData?.is_active}
      >
         <SidebarConversationsHeader
            search={search}
            onUpdateSearch={handleUpdateSearch}
            onClearSearch={handleClearSearch}
         />

         <Scrollbars
            ref={scrollRef}
            onScroll={() => {
               const currentScrollRef = scrollRef.current;

               if (
                  !currentScrollRef ||
                  loading ||
                  !currentScrollRef.getValues() ||
                  currentScrollRef.getValues().top < 0.9 ||
                  !data?.allConversations?.pageInfo?.hasNextPage
               ) {
                  return;
               }

               handleLoadMore();
            }}
         >
            <SidebarMenu isAlwaysShown>
               {Boolean(newConversation) && (
                  <SidebarMessage>
                     <Message to='/mes-echanges/mes-messages/nouveau'>
                        <Media>
                           <MediaImage>
                              <Avatar small style={{ flex: '0 0 32px' }} />
                           </MediaImage>

                           <MediaBody>
                              <MessageMeta isNewMessagePlaceholder>
                                 <DeletePlaceholderButton onClick={handleNewConversationDelete}>
                                    &times;
                                 </DeletePlaceholderButton>
                              </MessageMeta>

                              <MessageContent>
                                 <MessageAuthor>{t('communications:newMessage')}</MessageAuthor>
                              </MessageContent>
                           </MediaBody>
                        </Media>
                     </Message>
                  </SidebarMessage>
               )}

               {conversations.map(({ id, sqlId, preview, displayName, updatedAt, read, author }) => {
                  const avatarURL = getAttachmentURL((author || {}).avatarUrl);

                  return (
                     <SidebarMessage key={id}>
                        <Message to={`/mes-echanges/mes-messages/${sqlId}`}>
                           <Media>
                              <MediaImage>
                                 <Avatar {...author} avatarURL={avatarURL} small style={{ flex: '0 0 32px' }} />
                              </MediaImage>
                              <MediaBody>
                                 <MessageMeta isUnread={!read}>
                                    <TopicMetaUpdated isUnread={!read}>
                                       {formatRelativeDate(updatedAt, true)}
                                    </TopicMetaUpdated>
                                 </MessageMeta>
                                 <MessageContent isUnread={!read}>
                                    <MessageAuthor>{capitalizeFirstLetter(displayName)}</MessageAuthor>
                                    <MessageExcerpt isUnread={!read}>
                                       <LinesEllipsis
                                          text={preview || ''}
                                          maxLine={2}
                                          ellipsis='…'
                                          trimRight
                                          basedOn='letters'
                                       />
                                    </MessageExcerpt>
                                 </MessageContent>
                              </MediaBody>
                           </Media>
                        </Message>
                     </SidebarMessage>
                  );
               })}
            </SidebarMenu>
         </Scrollbars>
      </Sidebar>
   );
};

export default ConversationsSidebar;
